/* Product PDP — buy rail behaviour.
   Loaded only by the alternate product template. Two jobs: stop an add to cart
   until a size is chosen, and add to cart without a full page navigation so
   the theme's cart drawer can respond.

   The button is never visually disabled (13 Sep 2026). A 40%-opacity grey
   button above an unpicked size ladder read as broken or sold out. It now stays
   solid, carries aria-disabled, and a click with no size prompts for one and
   moves focus to the ladder. The markup ships the button enabled with the
   sizes `required`, so with this file blocked or broken the browser still asks
   for a size and the form posts; here the browser check is switched off in
   favour of the prompt below. A product with nothing in stock ships disabled
   and labelled "Sold out", and stays that way. */

class FlrcProductBuy extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submit = this.querySelector('[data-product-submit]');
    this.note = this.querySelector('[data-product-note]');
    this.sizeName = this.querySelector('[data-product-size-name]');
    this.stock = this.querySelector('[data-product-stock]');
    this.stockText = this.querySelector('[data-product-stock-text]');
    this.afterpay = this.querySelector('[data-product-afterpay]');
    this.afterpayText = this.querySelector('[data-product-afterpay-text]');
    this.sticky = this.querySelector('[data-product-sticky]');
    this.stickyButton = this.querySelector('[data-product-sticky-button]');
    if (!this.form || !this.submit) return;

    this.defaultLabel = this.submit.textContent.trim();
    this.sizes = this.querySelector('.flrc-product-buy__sizes');
    // Nothing in stock: the server rendered "Sold out", disabled. Leave it.
    this.soldOut = !this.form.querySelector('input[name="id"]:not(:disabled)');
    if (this.soldOut) return;
    this.form.noValidate = true;
    this.submit.disabled = false;

    this.form.addEventListener('change', () => this.#sync());
    this.form.addEventListener('submit', (event) => this.#add(event));

    this.#wireSticky();
    this.#sync();
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
  }

  // Sticky add to cart (phones; CSS hides the bar from 750px). It shows once the
  // main button has scrolled UP out of view — not merely while it is still
  // below the fold on load — and hides again while the footer is on screen so
  // it never covers it. Its button forwards to the main one, so the no-size
  // path (scroll to sizes, show the error) is the same code, not a copy.
  #wireSticky() {
    if (!this.sticky || !this.stickyButton || !('IntersectionObserver' in window)) return;

    let pastButton = false;
    let atFooter = false;
    const render = () => {
      const show = pastButton && !atFooter;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.stickyButton.tabIndex = show ? 0 : -1;
    };

    this.stickyObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === this.submit) {
          pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else {
          atFooter = entry.isIntersecting;
        }
      }
      render();
    });
    this.stickyObserver.observe(this.submit);
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (footer) this.stickyObserver.observe(footer);

    this.stickyButton.addEventListener('click', () => this.submit.click());
  }

  // Both buttons always carry the same label (Adding / Added / Add to cart).
  #label(text) {
    this.submit.textContent = text;
    if (this.stickyButton) this.stickyButton.textContent = text;
  }

  #busy(isBusy) {
    this.submit.disabled = isBusy;
    if (this.stickyButton) this.stickyButton.disabled = isBusy;
  }

  #selected() {
    return this.form.querySelector('input[name="id"]:checked');
  }

  #sync() {
    const chosen = this.#selected();
    this.submit.disabled = false;
    this.submit.setAttribute('aria-disabled', chosen ? 'false' : 'true');
    if (chosen) {
      if (this.note) this.note.textContent = '';
      this.sizes?.classList.remove('is-prompting');
    }

    // Spell out the chosen size code. The words come from the radio's own
    // data-size-name, resolved in Liquid from the section's size-names table —
    // there is no lookup table here to fall out of step with it. A code with no
    // entry blanks the line rather than echoing itself, which would just repeat
    // the button.
    if (this.sizeName) {
      this.sizeName.textContent = chosen ? chosen.dataset.sizeName || '' : '';
    }

    // Afterpay follows the chosen variant's price. Before a size is picked the
    // server-rendered line (the product's own price) stays as it is. A variant
    // outside Afterpay's range (data-afterpay empty) hides the line.
    if (this.afterpay && this.afterpayText && chosen) {
      const amount = chosen.dataset.afterpay || '';
      this.afterpay.hidden = amount === '';
      if (amount) {
        this.afterpayText.textContent =
          (this.afterpay.dataset.afterpayBefore || '') + amount + (this.afterpay.dataset.afterpayAfter || '');
      }
    }

    // The indicator describes the chosen size, so it is invisible until there
    // is one — a standing "In stock" above an empty size ladder says nothing.
    // Visibility rather than `hidden`: the line keeps its space, so picking a
    // size never pushes Add to cart down (the layout jump).
    if (this.stock) {
      this.stock.classList.toggle('is-visible', Boolean(chosen));
      if (chosen && this.stockText) {
        this.stockText.textContent = this.stock.dataset.inStock || 'In stock';
      }
    }
  }

  async #add(event) {
    event.preventDefault();
    const chosen = this.#selected();
    if (!chosen) {
      if (this.note) this.note.textContent = this.note.dataset.prompt || 'Select a size';
      if (this.sizes) {
        // Restart the cue on repeat clicks.
        this.sizes.classList.remove('is-prompting');
        void this.sizes.offsetWidth;
        this.sizes.classList.add('is-prompting');
      }
      this.form.querySelector('input[name="id"]:not(:disabled)')?.focus({ preventScroll: true });
      // The message sits under the sizes; bring that block into view. This is
      // also what the sticky bar relies on, since the sizes are above it.
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      (this.note || this.sizes)?.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      return;
    }

    this.#busy(true);
    this.#label(this.submit.dataset.busyLabel || 'Adding…');

    try {
      const res = await fetch(window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cart/add.js`
        : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(chosen.value), quantity: 1 }] }),
      });

      if (!res.ok) {
        // A 4xx carries Shopify's own reason ("All 2 Medium are in your
        // cart."). Say it here rather than posting the form to Shopify's bare
        // error page. Anything else falls back to the normal POST below.
        let reason = '';
        if (res.status >= 400 && res.status < 500) {
          try { reason = (await res.json()).description || ''; } catch { reason = ''; }
        }
        if (reason) {
          if (this.note) this.note.textContent = reason;
          this.#label(this.defaultLabel);
          this.#busy(false);
          return;
        }
        throw new Error(`cart/add ${res.status}`);
      }

      // Let the cart icon and drawer update themselves. 'cart:update' is the
      // theme's own event name (assets/events.js) and both listen on document.
      const cart = await (await fetch('/cart.js')).json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'flrc-product', data: { source: 'flrc-product' } },
        })
      );

      this.#label(this.submit.dataset.doneLabel || 'Added');
      setTimeout(() => {
        this.#label(this.defaultLabel);
        this.#busy(false);
        this.#sync();
      }, 1600);
    } catch (error) {
      console.error('[flrc-product] add to cart failed', error);
      // The form still has a real action, so fall back to a normal POST.
      this.form.submit();
    }
  }
}

if (!customElements.get('flrc-product-buy')) {
  customElements.define('flrc-product-buy', FlrcProductBuy);
}

/* Build-section gallery. One plate at a time, stepped left/right rather than
   scrolled. Every image ships in the markup with the first one marked active,
   so with JS unavailable the section still shows a plate instead of an empty
   frame. */
class FlrcProductGallery extends HTMLElement {
  connectedCallback() {
    this.images = Array.from(this.querySelectorAll('[data-product-img]'));
    if (!this.images.length) return;

    this.count = this.querySelector('[data-product-count]');
    this.caption = this.querySelector('[data-product-caption-out]');
    this.index = 0;

    // Two sets of controls step the same gallery: the labelled pair over the
    // top-right corner, and the edge zones. Both carry the same data attribute,
    // so query all of them rather than the first match.
    this.querySelectorAll('[data-product-prev]').forEach((button) =>
      button.addEventListener('click', () => this.#step(-1))
    );
    this.querySelectorAll('[data-product-next]').forEach((button) =>
      button.addEventListener('click', () => this.#step(1))
    );

    // Arrow keys work once anything inside the gallery has focus.
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.#step(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.#step(1); }
    });

    this.#wireSwipe();
    this.#show(0);

    // A single plate has nothing to step between — hide every control, not just
    // the corner pair, or the edge zones stay live over a static image.
    if (this.images.length < 2) {
      this.querySelectorAll('[data-product-control]').forEach((el) => el.setAttribute('hidden', ''));
    }
  }

  #step(direction) {
    const total = this.images.length;
    this.#show((this.index + direction + total) % total);
  }

  #show(next) {
    this.index = next;
    this.images.forEach((img, i) => {
      const active = i === next;
      img.classList.toggle('is-active', active);
      img.toggleAttribute('aria-hidden', !active);
    });

    if (this.count) {
      this.count.textContent = `${String(next + 1).padStart(2, '0')} / ${String(this.images.length).padStart(2, '0')}`;
    }

    if (this.caption) {
      const text = this.images[next].dataset.productCaption || '';
      this.caption.textContent = text;
      this.caption.hidden = text === '';
    }
  }

  #wireSwipe() {
    const frame = this.querySelector('.flrc-product-gallery__frame');
    if (!frame) return;
    let startX = null;

    frame.addEventListener('pointerdown', (event) => { startX = event.clientX; });
    frame.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      startX = null;
      // Ignore taps and vertical-ish drags; 40px is past an accidental nudge.
      if (Math.abs(dx) > 40) this.#step(dx < 0 ? 1 : -1);
    });
    frame.addEventListener('pointercancel', () => { startX = null; });
  }
}

if (!customElements.get('flrc-product-gallery')) {
  customElements.define('flrc-product-gallery', FlrcProductGallery);
}

/* Features rail. The track is a scroll-snap scroller and does the whole job on
   its own — this only drives the arrow pair: scroll by one card, disable each
   end, and hide the pair outright when every card already fits. */
class FlrcProductRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-product-rail-track]');
    if (!this.track) return;

    this.nav = this.querySelector('[data-product-rail-nav]');
    this.prev = this.querySelector('[data-product-rail-prev]');
    this.next = this.querySelector('[data-product-rail-next]');

    this.prev?.addEventListener('click', () => this.#scroll(-1));
    this.next?.addEventListener('click', () => this.#scroll(1));
    this.track.addEventListener('scroll', () => this.#sync(), { passive: true });

    // Card width is in vw, so what fits changes on resize — and in the theme
    // editor a block can be added without the element being reconnected.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.#sync());
      this.observer.observe(this.track);
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  // firstElementChild rather than a tag name: the features track is a list of
  // <li> and the product gallery a row of <figure>, and both use this element.
  #step() {
    const card = this.track.firstElementChild;
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  #scroll(direction) {
    this.track.scrollBy({ left: this.#step() * direction, behavior: 'smooth' });
  }

  #sync() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    // A pixel of slack throughout: scrollLeft and scrollWidth are fractional at
    // non-integer zoom, so exact comparisons leave an arrow live at the end.
    if (this.nav) this.nav.toggleAttribute('hidden', max <= 1);
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max - 1;
  }
}

if (!customElements.get('flrc-product-rail')) {
  customElements.define('flrc-product-rail', FlrcProductRail);
}

/* Spec table disclosure. The markup ships closed, which is what phones want, so
   this only has to open it from tablet up and follow the breakpoint after that.
   Sync runs on connect and on breakpoint crossings only — never on toggle — so
   a table the reader opened on a phone stays open. */
class FlrcProductDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;

    this.media = window.matchMedia('(min-width: 750px)');
    this.onChange = () => this.#sync();
    this.media.addEventListener('change', this.onChange);

    // Gates the CSS that hides the summary. Set only once the element is live,
    // so a page without script keeps a working control at every width.
    this.dataset.ready = '';
    this.#sync();
  }

  disconnectedCallback() {
    this.media?.removeEventListener('change', this.onChange);
  }

  #sync() {
    this.details.open = this.media.matches;
  }
}

if (!customElements.get('flrc-product-disclosure')) {
  customElements.define('flrc-product-disclosure', FlrcProductDisclosure);
}

/* Size guide modal. The trigger is a real link to the size guide page, so this
   is pure enhancement: intercept the click, fetch that page once, lift the
   anchored chart out of it and show it in a dialog. Anything that goes wrong —
   no JS, no dialog support, fetch fails, anchor missing — falls through to the
   link's own navigation, which is the behaviour that existed before.

   The chart lives only on the page. Nothing is duplicated into the product
   template, so editing a chart in the theme editor updates the modal too. */
(() => {
  const trigger = document.querySelector('[data-flrc-size-guide]');
  const modal = document.querySelector('[data-flrc-size-guide-modal]');
  if (!trigger || !modal || typeof modal.showModal !== 'function') return;

  const body = modal.querySelector('[data-flrc-size-guide-body]');
  const closeButton = modal.querySelector('[data-flrc-size-guide-close]');
  let cached = null;

  // showModal() focuses the first control, which is the units toggle, and
  // paints a focus ring on it before anyone has touched it. Focus the panel
  // instead: Tab still lands on the toggle next, with its ring.
  const open = () => {
    modal.showModal();
    modal.focus({ preventScroll: true });
    // Scroll lock: the page behind a modal dialog still scrolls on touch, so
    // pin it while the panel is open and release on any close (×, Esc, backdrop).
    document.body.classList.add('flrc-scroll-locked');
  };

  modal.addEventListener('close', () => {
    document.body.classList.remove('flrc-scroll-locked');
    // <dialog> restores focus to the opener on close; this is the backstop for
    // the cases where the opener has since been re-rendered.
    if (document.activeElement === document.body) trigger.focus({ preventScroll: true });
  });

  // Prefer a chart already in this page. templates/product.json carries the
  // size guide as a hidden `source_only` section, because the theme is
  // unpublished and Shopify's page-template dropdown reads the LIVE theme — so
  // a standalone guide page cannot be assigned yet. Reading locally also means
  // no network round trip when the modal opens.
  const localChart = (href) => {
    const anchor = anchorFrom(href);
    if (anchor) {
      const el = document.getElementById(anchor);
      if (el && el.hasAttribute('data-sizeguide-chart')) return el.outerHTML;
    }
    const all = document.querySelectorAll('[data-sizeguide-chart]');
    return all.length ? Array.from(all).map((el) => el.outerHTML).join('') : null;
  };

  const anchorFrom = (href) => {
    const url = new URL(href, window.location.origin);
    return url.hash ? decodeURIComponent(url.hash.slice(1)) : '';
  };

  const fetchChart = async (href) => {
    // Split the destination into page and anchor. No anchor means the whole
    // guide, which is the sensible fallback for a product that hasn't been
    // pointed at a specific chart yet.
    const url = new URL(href, window.location.origin);
    const anchor = url.hash ? decodeURIComponent(url.hash.slice(1)) : '';
    const response = await fetch(url.pathname + url.search, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`size guide fetch failed: ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

    if (anchor) {
      const target = doc.getElementById(anchor);
      if (target) return target.outerHTML;
    }
    const all = doc.querySelectorAll('[data-sizeguide-chart]');
    if (all.length) return Array.from(all).map((el) => el.outerHTML).join('');
    throw new Error('no size guide chart found');
  };

  trigger.addEventListener('click', async (event) => {
    // Let modified clicks (new tab, download, middle-click) behave normally.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();

    if (cached === null) {
      // Local first — no network, and it works before the guide page exists.
      cached = localChart(trigger.getAttribute('href'));
    }

    if (cached === null) {
      body.innerHTML = '<p class="flrc-sizeguide-modal__fallback">Loading…</p>';
      open();
      try {
        cached = await fetchChart(trigger.getAttribute('href'));
      } catch (error) {
        // Don't strand the user in an empty modal — send them to the real page.
        modal.close();
        window.location.href = trigger.getAttribute('href');
        return;
      }
    } else {
      open();
    }
    body.innerHTML = cached;
    window.FlrcSizeUnits?.apply(body);
    body.scrollTop = 0;
  });

  closeButton?.addEventListener('click', () => modal.close());

  // Click outside the panel closes it. <dialog> puts the backdrop click on the
  // dialog element itself, so compare against its own box.
  modal.addEventListener('click', (event) => {
    if (event.target !== modal) return;
    const box = modal.getBoundingClientRect();
    const outside =
      event.clientX < box.left ||
      event.clientX > box.right ||
      event.clientY < box.top ||
      event.clientY > box.bottom;
    if (outside) modal.close();
  });
})();
